import { useLayoutEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { PRINT_BORDER } from '../printStyles';
import LabelCell from './LabelCell';
import { summaryCellSx, summaryValueSx } from './styles';

/**
 * Design keeps at least a third of the summary when the type still fits beside
 * it. `100cqw` resolves against the summary grid (`containerType: 'inline-size'`).
 * When the two values cannot share the row, each takes a full line.
 */
const DESIGN_MIN_WIDTH = 'calc(100cqw / 3)';

const nowrapValueSx = {
  ...summaryValueSx,
  whiteSpace: 'nowrap',
} as const;

const measureValueSx = {
  ...nowrapValueSx,
  minWidth: 'max-content',
} as const;

const measureCellSx = {
  ...summaryCellSx,
  flex: '0 0 auto',
  width: 'max-content',
  minWidth: 'max-content',
} as const;

interface TypeDesignRowProps {
  title: string;
  designCode: string | null;
}

const TypeDesignRow = ({ title, designCode }: TypeDesignRowProps) => {
  const columnRef = useRef<HTMLDivElement>(null);
  const typeRef = useRef<HTMLDivElement>(null);
  const designRef = useRef<HTMLDivElement>(null);
  const [stacked, setStacked] = useState(false);

  useLayoutEffect(() => {
    const column = columnRef.current;
    const typeEl = typeRef.current;
    const designEl = designRef.current;
    if (designCode == null || !column || !typeEl || !designEl) {
      return;
    }

    const update = () => {
      const available = column.clientWidth;
      if (available === 0) {
        return;
      }
      const typeWidth = typeEl.offsetWidth;
      const designWidth = designEl.offsetWidth;
      const summaryWidth = column.parentElement?.clientWidth ?? available;
      const designFloor = summaryWidth / 3;
      const nextStacked =
        typeWidth + Math.max(designWidth, designFloor) > available;
      setStacked((current) =>
        current === nextStacked ? current : nextStacked
      );
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(column);
    return () => observer.disconnect();
  }, [title, designCode]);

  if (designCode == null) {
    return (
      <>
        <LabelCell sx={{ borderBottom: PRINT_BORDER }}>Type</LabelCell>
        <Box
          sx={{
            ...summaryCellSx,
            gridColumn: '2 / -1',
            borderBottom: PRINT_BORDER,
          }}
        >
          <Typography sx={{ ...summaryValueSx, overflowWrap: 'break-word' }}>
            {title}
          </Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      <LabelCell sx={{ borderBottom: PRINT_BORDER }}>Type</LabelCell>
      <Box
        ref={columnRef}
        sx={{
          position: 'relative',
          display: 'flex',
          gridColumn: '2 / -1',
          minWidth: 0,
          borderBottom: PRINT_BORDER,
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            height: 0,
            overflow: 'hidden',
            clipPath: 'inset(50%)',
            opacity: 0,
            pointerEvents: 'none',
          }}
        >
          <Box ref={typeRef} sx={measureCellSx}>
            <Typography sx={measureValueSx}>{title}</Typography>
          </Box>
          <Box
            ref={designRef}
            sx={{
              display: 'inline-flex',
              width: 'max-content',
              borderLeft: PRINT_BORDER,
            }}
          >
            <LabelCell sx={{ flexShrink: 0 }}>Design</LabelCell>
            <Box sx={measureCellSx}>
              <Typography sx={measureValueSx}>{designCode}</Typography>
            </Box>
          </Box>
        </Box>

        {stacked ? (
          <Box sx={{ ...summaryCellSx, flex: 1 }}>
            <Typography sx={{ ...summaryValueSx, overflowWrap: 'break-word' }}>
              {title}
            </Typography>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                ...summaryCellSx,
                flex: '1 1 auto',
                minWidth: 0,
                overflow: 'hidden',
                maxWidth: `calc(100% - ${DESIGN_MIN_WIDTH})`,
              }}
            >
              <Typography sx={nowrapValueSx}>{title}</Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flex: '0 0 auto',
                minWidth: `max(${DESIGN_MIN_WIDTH}, max-content)`,
                borderLeft: PRINT_BORDER,
              }}
            >
              <LabelCell sx={{ flexShrink: 0 }}>Design</LabelCell>
              <Box sx={{ ...summaryCellSx, flex: '1 1 auto' }}>
                <Typography sx={nowrapValueSx}>{designCode}</Typography>
              </Box>
            </Box>
          </>
        )}
      </Box>
      {stacked ? (
        <>
          <LabelCell sx={{ borderBottom: PRINT_BORDER }}>Design</LabelCell>
          <Box
            sx={{
              ...summaryCellSx,
              gridColumn: '2 / -1',
              borderBottom: PRINT_BORDER,
            }}
          >
            <Typography sx={{ ...summaryValueSx, overflowWrap: 'break-word' }}>
              {designCode}
            </Typography>
          </Box>
        </>
      ) : null}
    </>
  );
};

export default TypeDesignRow;
